/*
 * Copyright (C) 2025 Musarubra US LLC.  All Rights Reserved.
 */

import { OktaSignIn, WidgetOptions } from '@okta/okta-signin-widget';
import { iamLogin } from '../main';
import { WidgetContext } from '../widget-contexts/widget-context-base';
import { AccountUnlockWidgetContext } from '../widget-contexts/account-unlock';
import { ActivateTotpWidgetContext } from '../widget-contexts/activate-totp';
import { BarcodeTotpWidgetContext } from '../widget-contexts/barcode-totp';
import { EnrollCallWidgetContext } from '../widget-contexts/enroll-call';
import { EnrollChoicesWidgetContext } from '../widget-contexts/enroll-choices';
import { EnrollManualTotpWidgetContext } from '../widget-contexts/enroll-manual-totp';
import { EnrollSmsWidgetContext } from '../widget-contexts/enroll-sms';
import { EnrollTotpWidgetContext } from '../widget-contexts/enroll-totp';
import { MfaVerifyWidgetContext } from '../widget-contexts/mfa-verify';
import { PasswordExpiredWidgetContext } from '../widget-contexts/password-expired';
import { PasswordResetWidgetContext } from '../widget-contexts/password-reset';
import { PrimaryAuthWidgetContext } from '../widget-contexts/primary-auth';
import { RecoveryQuestionWidgetContext } from '../widget-contexts/recovery-question';
import { GenericWidgetContext } from '../widget-contexts/generic';
import { iamLocalization } from '../localization';
import { LoginWidget } from './login-widget-base';
import { iam } from '../iam';
import { WidgetElements } from '../elements/widget-elements-base';

export class OktaLoginWidget implements LoginWidget {
  private initialized = false;
  private contextName: string | null = null;
  private context: WidgetContext | null = null;
  private widget: OktaSignIn;
  private config: WidgetOptions;
  private afterRenderSpecific: () => void;
  private afterErrorSpecific: (context: any, error: any) => void;
  private errorMessageDetected: (error: string) => boolean;
  private renderSuccess: (res: any) => void;
  private renderError: (error: any) => void;

  constructor(public elements: WidgetElements) {}

  public getInitialized(): boolean {
    return this.initialized;
  }

  public setInitialized(value: boolean) {
    this.initialized = value;
  }

  public getContextName(): string | null {
    return this.contextName;
  }

  /**
   * responsible for loading the widget into the page (login.html and updatepassword.html)
   * @param config widget config settings
   * @param afterRenderSpecific callback for page specific post-render initializing
   * @param afterErrorSpecific callback for page specific widget error handling
   * @param errorMessageDetected callback for processing error messages that the widget wants to display (by default, message displayed in banner unless callback returns true)
   * @param renderSuccess callback when the widget renders successfully
   * @param renderError callback when the widget fails to render
   */
  public initialize(
    config: WidgetOptions,
    afterRenderSpecific: () => void,
    afterErrorSpecific: (context: any, error: any) => void,
    errorMessageDetected: (error: string) => boolean,
    renderSuccess: (res: any) => void,
    renderError: (error: any) => void
  ) {
    this.config = config;
    this.afterRenderSpecific = afterRenderSpecific;
    this.afterErrorSpecific = afterErrorSpecific;
    this.errorMessageDetected = errorMessageDetected;
    this.renderSuccess = renderSuccess;
    this.renderError = renderError;
    this.render();
  }

  public render() {
    let inputValues: { [name: string]: string } = {};
    if (this.widget) {
      // the widget is already present, so we're re-rendering (necessary for locale change to propagate to the widget)
      if (iam.settings.debug) console.log('Re-rendering okta widget');
      // capture input values so they can be re-inserted after re-rendering
      iamLogin.page.elements.getFieldsetContainer().querySelectorAll<HTMLInputElement>('input[type="text"], input[type="tel"], input[type="password"]').forEach(input => {
        inputValues[input.name] = input.value;
      });
      // remove widget: off() unbinds our afterRender/afterError handlers so they don't also run for the new widget
      // (the old afterRender handler would take over the new widget's first render without re-inserting the input
      // values), and remove() takes the widget's view out of the page
      this.widget.off();
      this.widget.remove();
      // a new context is created for the new widget, so let the current one clean up after itself
      this.context?.deinitialize();
      this.context = this.contextName = null;
      iamLogin.pageInitialized = false;
    }

    const FULL_LANGS = ['nl_NL', 'pt_BR', 'zh_CH', 'zh_TW'];
    let language = iamLocalization.getLocale();
    if (FULL_LANGS.includes(language)) {
      language = language.replace('_', '-');
    } else {
      language = language.split('_')[0];
    }
    this.config.language = language;

    if (iam.settings.debug) console.log('Instantiating okta widget with language', this.config.language);
    this.widget = new OktaSignIn(this.config);

    this.widget.on('afterRender', (context: { controller: string }) => {
      if (iam.settings.debug) console.log('Okta afterRender');
      const postProcessContextSwitch = (contextName: unknown) => {
        // new widget context established
        if (typeof contextName !== 'string') {
          if (iam.settings.debug) console.warn('Widget render context isn\'t a string. Okta bug?');
          contextName = this.elements.getContextFromWidget();
        }
        iamLogin.localizationCallbacks = iamLogin.localizationCallbacks.filter(registration => !registration.unregisterOnContextTransition);
        // is this a second pass on the same context (due to css transitioning, before and after) or the first pass?
        if (contextName !== this.contextName || this.context?.hasContextMutated()) {
          // first pass, so do our initialization
          if (contextName !== this.contextName) { // don't re-create context object if it's just mutated (mfa-verify)
            // give prior context a chance to clean itself up
            this.context?.deinitialize();
            this.createContext(contextName as string);
          }
          document.getElementById('loading-content').style.display = 'none';
          document.getElementById('right-pane-contents').style.display = '';
          iamLogin.page.elements.getContainer().className = this.contextName === 'enroll-choices' ? 'wide' : '';
          // initialization that's specific to the widget's context
          this.context.initialize();
          // re-rendering the widget, so re-insert form values
          Object.keys(inputValues).forEach(inputFieldName => {
            const field = iamLogin.page.elements.getFieldsetContainer().querySelector<HTMLInputElement>('input[name="' + inputFieldName + '"]');
            if (field) {
              field.value = inputValues[inputFieldName];
            }
          });
          inputValues = {};
          // disable submit button assuming the form values aren't yet valid
          this.context.validateForm();
        } else {
          if (iam.settings.debug) console.log('Widget render context ' + contextName + ' already loaded');
        }
        // this doesn't always work during the pre-render phase, so do it in both cases for good measure
        this.context.setInitialFocus();
      };
      if (!iamLogin.pageInitialized) {
        // set up mutation observer to watch the child of the auth-content-inner div
        // because that child's class includes the context name, and this allows us to do
        // our post processing before okta's css transition completes (e.g. password to mfa).
        (new MutationObserver((mutationList, observer) => {
          for (let i = 0; i < mutationList.length; i++) {
            for (let j = 0; j < mutationList[i].addedNodes.length; j++) {
              // widget is css transitioning to a new context - this is the early detection of that so we can make
              // visual adjustments without having to wait until the transition is complete ('afterRender').
              // the element's class equals the name of the widget's context, so grab the name and process accordingly.
              if (mutationList[i].addedNodes[j] as Element) {
                if (Array.from((mutationList[i].addedNodes[j] as Element).classList).some(one => {
                  if (this.context.supported) {
                    if (iam.settings.debug) console.log('Okta pre-render detected');
                    postProcessContextSwitch(one);
                    return true;
                  } else {
                    return false;
                  }
                })) {
                  // found a context class name, so break out of loop
                  return;
                }
              }
            }
          }
        })).observe(this.elements.getContextParent(), { childList: true });
      } else {
        // transitioning between contexts, so if there's an error message, hide it
        iamLogin.hideMessageBanners();
      }
      this.configureOktaErrorWatcher(this.errorMessageDetected);
      postProcessContextSwitch(context.controller);
      // let the passed-in afterRender handler do its thing
      if (this.afterRenderSpecific) {
        this.afterRenderSpecific();
      }
    });

    this.widget.on('afterError', (context: any, error: any) => {
      if (iam.settings.debug) console.log('afterError', context, error);
      // typically this will occur after hitting submit, so if it comes back with an error, set focus back to the primary input field
      if (iam.settings.debug) console.log('Setting focus to input field after error');
      this.context.setInitialFocus();
      // let the passed-in afterError handler do its thing
      if (this.afterErrorSpecific) {
        this.afterErrorSpecific(context, error);
      }
    });

    this.widget.renderEl({ el: '#widget-container' }, this.renderSuccess, this.renderError);
  }

  public configureOktaErrorWatcher(errorMessageDetected: (error: string) => boolean) {
    // set up a mutation observer to watch for error messages inside the widget, which will be copied to the error banner at the top of the page
    const container = this.elements.getErrorContainer();
    (new MutationObserver((mutationList, observer) => {
      if (iam.settings.debug) console.log('error message mutations detected: ' + mutationList.length);
      mutationList.forEach(mutation => {
        // watch for added nodes only. we clear error message during context transition and when submit occurs.
        // clearing of the message (i.e. removedNodes) doesn't seem to be necessary at other times.
        // we also want to avoid clearing an error message when re-rendering the widget after a language change.
        if (mutation.addedNodes.length > 0) {
          if (iam.settings.debug) console.log('error message detected: ', container.textContent);
          // first let the passed in error handler have a look, and if it returns true, no further handling should occur
          if (!errorMessageDetected || !errorMessageDetected(container.textContent)) {
            // there can be more than one error message and that's determined by the direct children
            const messages: string[] = [];
            (Array.from(container.children) as HTMLElement[]).forEach(child => {
              if (child.textContent) {
                messages.push(child.textContent);
              }
            });
            messages.forEach(message => {
              iamLogin.showErrorMessage(message);
            });
            // hide okta's message since we're showing it up top
            container.style.display = 'none';
          }
        } else {
          if (iam.settings.debug) console.log('skipping error message node removal change: ', mutation.removedNodes);
        }
      });
    })).observe(container, { childList: true });
  }

  public localeChanged(newLocale: string) {
    this.render();
  }

  public createContext(contextName: string) {
    this.contextName = contextName;
    switch (contextName) {
      case 'account-unlock':
        this.context = new AccountUnlockWidgetContext(this.elements);
        break;
      case 'activate-totp':
        this.context = new ActivateTotpWidgetContext(this.elements);
        break;
      case 'barcode-totp':
        this.context = new BarcodeTotpWidgetContext(this.elements);
        break;
      case 'enroll-call':
        this.context = new EnrollCallWidgetContext(this.elements);
        break;
      case 'enroll-choices':
        this.context = new EnrollChoicesWidgetContext(this.elements);
        break;
      case 'enroll-manual-totp':
        this.context = new EnrollManualTotpWidgetContext(this.elements);
        break;
      case 'enroll-sms':
        this.context = new EnrollSmsWidgetContext(this.elements);
        break;
      case 'enroll-totp':
        this.context = new EnrollTotpWidgetContext(this.elements);
        break;
      case 'mfa-verify':
        this.context = new MfaVerifyWidgetContext(this.elements);
        break;
      case 'password-expired':
        this.context = new PasswordExpiredWidgetContext(this.elements);
        break;
      case 'password-reset':
        this.context = new PasswordResetWidgetContext(this.elements);
        break;
      case 'primary-auth':
        this.context = new PrimaryAuthWidgetContext(this.elements);
        break;
      case 'recovery-question':
        this.context = new RecoveryQuestionWidgetContext(this.elements);
        break;
      default:
        console.warn(`Unrecognized context ${contextName}`);
        this.context = new GenericWidgetContext(this.elements);
        break;
    }
  }

  public validateForm() {
    this.context?.validateForm();
  }

  public localize() {
    this.context?.localize();
  }
}
